import React, { useState, useEffect, useCallback } from 'react';
import MonacoEditor from '@monaco-editor/react';
import { Button, Tooltip, message, theme } from 'antd';
import { EyeOutlined, EditOutlined, SaveOutlined, FolderOpenOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { useMarkdownActions, useMarkdowns } from '../../store/store';
import useTabManagerStore from '../../store/tabManagerStore';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('MarkdownViewer');
import { useDarkMode } from '../../contexts/DarkModeContext';
import FileBrowserDialog from '../FileBrowserDialog';

// Type definitions
interface MarkdownViewerProps {
  tabId: string;
}

// Styled component for markdown preview
const MarkdownPreview = styled.div<{ $codeBg?: string; $borderColor?: string; $textSecondary?: string; $linkColor?: string }>`
  padding: 16px;
  height: 100%;
  overflow: auto;
  
  h1, h2, h3, h4, h5, h6 {
    margin-top: 1.5em;
    margin-bottom: 0.5em;
    font-weight: 600;
    line-height: 1.25;
  }
  
  h1 {
    font-size: 2em;
    border-bottom: 1px solid ${props => props.$borderColor || '#eaecef'};
    padding-bottom: 0.3em;
  }
  
  h2 {
    font-size: 1.5em;
    border-bottom: 1px solid ${props => props.$borderColor || '#eaecef'};
    padding-bottom: 0.3em;
  }
  
  h3 {
    font-size: 1.25em;
  }
  
  p {
    margin-top: 0;
    margin-bottom: 1em;
    line-height: 1.6;
  }
  
  ul, ol {
    margin-top: 0;
    margin-bottom: 1em;
    padding-left: 2em;
  }
  
  li {
    margin-bottom: 0.25em;
  }
  
  code {
    background: ${props => props.$codeBg || '#f6f8fa'};
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-size: 0.85em;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
  }
  
  pre {
    background: ${props => props.$codeBg || '#f6f8fa'};
    padding: 16px;
    border-radius: 6px;
    overflow: auto;
    margin-bottom: 1em;
    
    code {
      background: transparent;
      padding: 0;
    }
  }
  
  blockquote {
    margin: 0 0 1em 0;
    padding: 0 1em;
    border-left: 0.25em solid ${props => props.$borderColor || '#dfe2e5'};
    color: ${props => props.$textSecondary || '#6a737d'};
  }
  
  table {
    border-collapse: collapse;
    margin-bottom: 1em;
    width: 100%;
  }
  
  table th,
  table td {
    padding: 6px 13px;
    border: 1px solid ${props => props.$borderColor || '#dfe2e5'};
  }
  
  table th {
    font-weight: 600;
    background: ${props => props.$codeBg || '#f6f8fa'};
  }
  
  table tr:nth-child(2n) {
    background: ${props => props.$codeBg || '#f6f8fa'};
  }
  
  a {
    color: ${props => props.$linkColor || '#0366d6'};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  hr {
    height: 0.25em;
    padding: 0;
    margin: 24px 0;
    background-color: ${props => props.$borderColor || '#e1e4e8'};
    border: 0;
  }
  
  img {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const MarkdownViewer: React.FC<MarkdownViewerProps> = ({ tabId }) => {
  const { token } = theme.useToken();
  const { isDarkMode } = useDarkMode();
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [localContent, setLocalContent] = useState<string>('');
  const [lastSavedContent, setLastSavedContent] = useState<string>('');
  const [saveAsDialogOpen, setSaveAsDialogOpen] = useState<boolean>(false);

  // Get markdown actions and content from store
  const { updateMarkdown } = useMarkdownActions();
  const markdowns = useMarkdowns();

  // Get tab manager store methods
  const { updateTabModified, updateTabPath, updateTabName, updateTabIsNew, tabs } = useTabManagerStore((state) => ({
    updateTabModified: state.updateTabModified,
    updateTabPath: state.updateTabPath,
    updateTabName: state.updateTabName,
    updateTabIsNew: state.updateTabIsNew,
    tabs: state.tabs,
  }));

  // Get current tab's modified state
  const currentTab = tabs.find(tab => tab.id === tabId);
  const isTabModified = currentTab?.isModified ?? false;

  // Find the current markdown content
  const currentMarkdown = markdowns.find((md) => md.id === tabId);

  // Update local content when markdown changes
  useEffect(() => {
    if (currentMarkdown) {
      setLocalContent(currentMarkdown.content);
      // If tab is already marked as modified (new file), keep lastSavedContent empty
      // Otherwise (existing file), set it to current content
      if (isTabModified) {
        setLastSavedContent(''); // New file - nothing has been saved yet
      } else {
        setLastSavedContent(currentMarkdown.content); // Existing file - content is already saved
      }
    }
  }, [currentMarkdown, isTabModified]);

  // Handler for Save button - routes to appropriate save logic based on isNew flag
  const handleSave = useCallback(async () => {
    const currentTab = tabs.find(tab => tab.id === tabId);
    
    // If this is a new file (not yet saved), open the file browser dialog
    if (currentTab?.isNew) {
      setSaveAsDialogOpen(true);
      return;
    }
    
    // For existing files with a saved path, save directly
    if (currentTab?.filePath) {
      try {
        const { filesService } = await import('../../api/services/files');
        
        // Save markdown to backend
        await filesService.saveFile(currentTab.filePath, localContent);
        
        // Update the markdown in store
        updateMarkdown(tabId, { content: localContent });
        
        // Mark as saved
        updateTabModified(tabId, false);
        setLastSavedContent(localContent);
        
        message.success('Markdown saved successfully');
        logger.info('Markdown saved to existing path:', { 
          tabId, 
          path: currentTab.filePath,
          contentLength: localContent.length 
        });
      } catch (error) {
        message.error('Failed to save markdown');
        logger.error('Failed to save markdown:', error);
      }
    } else {
      // No saved path yet - open Save As dialog (fallback)
      setSaveAsDialogOpen(true);
    }
  }, [tabId, tabs, localContent, updateMarkdown, updateTabModified]);

  const handleContentChange = (value: string | undefined): void => {
    const newValue = value || '';
    setLocalContent(newValue);
    
    // Update content in store
    updateMarkdown(tabId, { content: newValue });
    
    // Mark tab as modified if content has changed from last saved
    const hasChanged = newValue !== lastSavedContent;
    if (hasChanged !== isTabModified) {
      updateTabModified(tabId, hasChanged);
    }
  };

  const handleSaveAs = async (path: string): Promise<void> => {
    try {
      // Import the files service
      const { filesService } = await import('../../api/services/files');
      
      // Save markdown to backend
      await filesService.saveFile(path, localContent);
      
      // Update tab metadata to reflect the saved file
      const filename = path.split('/').pop() || 'document';
      const displayName = filename.replace(/\.md$/, '');
      
      updateTabName(tabId, displayName);
      updateTabPath(tabId, path);
      updateTabModified(tabId, false);
      updateTabIsNew(tabId, false); // Mark as no longer new
      setLastSavedContent(localContent);
      
      // Update the markdown in store
      updateMarkdown(tabId, { content: localContent });
      
      message.success(`Markdown saved as ${path}`);
      
      logger.info('Markdown saved and tab updated:', { 
        tabId, 
        path,
        filename: displayName,
        contentLength: localContent.length 
      });
    } catch (error) {
      logger.error('Failed to save markdown:', error);
      message.error('Failed to save markdown');
      throw error;
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '8px',
          borderBottom: `1px solid ${token.colorBorder}`,
          backgroundColor: token.colorBgContainer,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title={isTabModified ? "Save Markdown" : "No changes to save"} placement="bottom">
            <Button 
              type="text" 
              icon={<SaveOutlined />} 
              onClick={handleSave}
              disabled={!isTabModified}
              data-testid="save-button"
            />
          </Tooltip>
          <Tooltip title="Save As..." placement="bottom">
            <Button 
              type="text" 
              icon={<FolderOpenOutlined />} 
              onClick={() => setSaveAsDialogOpen(true)}
              data-testid="save-as-button"
            />
          </Tooltip>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title={isPreview ? 'Edit' : 'Preview'} placement="bottom">
            <Button
              type="text"
              icon={isPreview ? <EditOutlined /> : <EyeOutlined />}
              onClick={() => setIsPreview(!isPreview)}
            />
          </Tooltip>
        </div>
      </div>
      <div style={{ flex: 1, position: 'relative' }}>
        {isPreview ? (
          <MarkdownPreview
            style={{ backgroundColor: token.colorBgContainer, color: token.colorText }}
            $codeBg={token.colorFillAlter}
            $borderColor={token.colorBorder}
            $textSecondary={token.colorTextSecondary}
            $linkColor={token.colorPrimary}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{localContent}</ReactMarkdown>
          </MarkdownPreview>
        ) : (
          <MonacoEditor
            height="100%"
            language="markdown"
            theme={isDarkMode ? 'vs-dark' : 'vs-light'}
            value={localContent}
            onChange={handleContentChange}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              wordWrap: 'on',
              scrollBeyondLastLine: false,
              padding: { top: 8, bottom: 8 },
              lineNumbers: 'on',
              folding: true,
              glyphMargin: true,
              lineDecorationsWidth: 5,
              lineNumbersMinChars: 3,
            }}
          />
        )}
      </div>
      <FileBrowserDialog
        open={saveAsDialogOpen}
        onCancel={() => setSaveAsDialogOpen(false)}
        onSave={handleSaveAs}
        title="Save Markdown As"
        fileExtension=".md"
        mode="save"
      />
    </div>
  );
};

export default MarkdownViewer;
